import { api } from '../../shared/api/client'
import type {
  ApplyMarketplaceBundleChangesInput,
  ApplyMarketplaceBundleChangesResult,
  CreateMarketplaceBundleInput,
  DeleteMarketplaceBundleResult,
  DiscardMarketplaceBundleChangesInput,
  DiscardMarketplaceBundleChangesResult,
  GetMarketplaceBundleChangesParams,
  ImportMarketplaceBundlesInput,
  ImportMarketplaceBundlesResult,
  ListMarketplaceBundleCandidatesParams,
  ListMarketplaceBundlesParams,
  MarketplaceBundle,
  MarketplaceBundleCandidatesPage,
  MarketplaceBundleCardsPage,
  MarketplaceBundleChange,
  MarketplaceBundleChanges,
  MarketplaceBundleCounters,
  MarketplaceBundleDetail,
  MarketplaceBundlesPage,
  MarketplaceBundleStats,
  UpdateMarketplaceBundleInput,
} from './types'

const EMPTY_COUNTERS: MarketplaceBundleCounters = {
  all: 0,
  pending: 0,
  incomplete: 0,
  unnamed: 0,
  free_cards: 0,
}

const EMPTY_STATS: MarketplaceBundleStats = {
  bundles: 0,
  cards_in_bundles: 0,
  free_cards: 0,
  incomplete: 0,
  errors: 0,
}

/** Gateway responses may be returned directly or inside the standard data envelope. */
const unwrap = (response: any): any => response?.data?.data ?? response?.data ?? {}

const finiteNumber = (value: unknown, fallback = 0): number => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

const normalizeNumbers = <T extends object>(source: unknown, defaults: T): T => {
  const record = source && typeof source === 'object' ? (source as Record<string, unknown>) : {}
  return Object.fromEntries(
    Object.entries(defaults as Record<string, number>).map(([key, fallback]) => [
      key,
      finiteNumber(record[key], fallback),
    ]),
  ) as T
}

const normalizeBundlesPage = (response: any): MarketplaceBundlesPage => {
  const data = unwrap(response)
  return {
    items: Array.isArray(data.items) ? (data.items as MarketplaceBundle[]) : [],
    total: finiteNumber(data.total),
    counters: normalizeNumbers(data.counters, EMPTY_COUNTERS),
    stats: normalizeNumbers(data.stats, EMPTY_STATS),
  }
}

const normalizeChanges = (response: any): MarketplaceBundleChanges => {
  const data = unwrap(response)
  const estimate = data.estimate && typeof data.estimate === 'object' ? data.estimate : {}
  return {
    total: finiteNumber(data.total),
    awaiting: finiteNumber(data.awaiting),
    bundles: finiteNumber(data.bundles),
    soft_limit: finiteNumber(data.soft_limit, 100),
    over_soft_limit: data.over_soft_limit === true,
    estimate: {
      requests: finiteNumber(estimate.requests),
      seconds: finiteNumber(estimate.seconds),
    },
    items: Array.isArray(data.items) ? (data.items as MarketplaceBundleChange[]) : [],
  }
}

const normalizeBundleDetail = (response: any): MarketplaceBundleDetail => {
  const data = unwrap(response)
  const bundle = data.bundle && typeof data.bundle === 'object' ? data.bundle : data
  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(bundle.items)
      ? bundle.items
      : []
  return { ...bundle, items } as MarketplaceBundleDetail
}

const normalizeCandidates = (response: any): MarketplaceBundleCandidatesPage => {
  const data = unwrap(response)
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: finiteNumber(data.total),
    limit: finiteNumber(data.limit, 50),
    offset: finiteNumber(data.offset),
    groups: Array.isArray(data.groups)
      ? data.groups.map((group: any) => ({
          key: String(group?.key ?? ''),
          total: finiteNumber(group?.total),
        }))
      : [],
    not_found: Array.isArray(data.not_found)
      ? data.not_found.filter((article: unknown): article is string => typeof article === 'string')
      : [],
  }
}

export const marketplaceBundlesAPI = {
  async list(
    params: ListMarketplaceBundlesParams,
    signal?: AbortSignal,
  ): Promise<MarketplaceBundlesPage> {
    const response = await api.get('/bundles', { params, signal })
    return normalizeBundlesPage(response)
  },

  /** Товары склеек порциями: строки таблицы вкладки. */
  async cards(
    params: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<MarketplaceBundleCardsPage> {
    const response = await api.get('/bundles/cards', { params, signal })
    const data = unwrap(response)
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: finiteNumber(data.total, 0),
      limit: finiteNumber(data.limit, 200),
      offset: finiteNumber(data.offset, 0),
      has_more: Boolean(data.has_more),
      articles_matched: Array.isArray(data.articles_matched) ? data.articles_matched : undefined,
    }
  },

  /**
   * Проверка списка артикулов на сервере: тот же запрос товаров склеек с
   * articles_resolve=1 — сервер игнорирует прочие фильтры (кроме площадки и
   * кабинета) и возвращает подмножество переданных значений, для которых
   * нашлась карточка. `query` обязан нести те же `marketplace`/`seller_id`,
   * что и обычный список — иначе резолв уйдёт за пределы кабинета.
   */
  async resolveArticles(
    query: Record<string, unknown>,
    values: string[],
  ): Promise<string[]> {
    const response = await api.get('/bundles/cards', {
      params: { ...query, articles: values.join(','), articles_resolve: '1', limit: 1 },
    })
    const data = unwrap(response)
    return Array.isArray(data?.articles_matched) ? data.articles_matched : []
  },

  async changes(
    params: GetMarketplaceBundleChangesParams,
    signal?: AbortSignal,
  ): Promise<MarketplaceBundleChanges> {
    const response = await api.get('/bundles/changes', { params, signal })
    return normalizeChanges(response)
  },

  async get(bundleId: number, signal?: AbortSignal): Promise<MarketplaceBundleDetail> {
    const response = await api.get(`/bundles/${bundleId}`, { signal })
    return normalizeBundleDetail(response)
  },

  async candidates(
    params: ListMarketplaceBundleCandidatesParams,
    signal?: AbortSignal,
  ): Promise<MarketplaceBundleCandidatesPage> {
    const response = await api.get('/bundles/candidates', { params, signal })
    return normalizeCandidates(response)
  },

  async create(input: CreateMarketplaceBundleInput): Promise<MarketplaceBundleDetail> {
    const response = await api.post('/bundles', input)
    return normalizeBundleDetail(response)
  },

  async update(
    bundleId: number,
    input: UpdateMarketplaceBundleInput,
  ): Promise<MarketplaceBundleDetail> {
    const response = await api.put(`/bundles/${bundleId}`, input)
    return normalizeBundleDetail(response)
  },

  async remove(bundleId: number, confirm = false): Promise<DeleteMarketplaceBundleResult> {
    const response = confirm
      ? await api.delete(`/bundles/${bundleId}`, { data: { confirm: true } })
      : await api.delete(`/bundles/${bundleId}`)
    return unwrap(response) as DeleteMarketplaceBundleResult
  },

  async discard(
    input: DiscardMarketplaceBundleChangesInput,
  ): Promise<DiscardMarketplaceBundleChangesResult> {
    const response = await api.post('/bundles/changes/discard', input)
    return unwrap(response) as DiscardMarketplaceBundleChangesResult
  },

  async applyMarketplaceBundleChanges(
    input: ApplyMarketplaceBundleChangesInput,
  ): Promise<ApplyMarketplaceBundleChangesResult> {
    const response = await api.post('/bundles/apply', input)
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },

  async importMarketplaceBundles(
    input: ImportMarketplaceBundlesInput,
  ): Promise<ImportMarketplaceBundlesResult> {
    const response = await api.post('/bundles/import', input)
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },
}
